import {
  downloadFile,
  downloadFiles,
  IAttachment,
  IUploadEvents,
  IUploadWarn,
  UPLOAD_CODES,
  UploadOper,
  UploadWarnCodes,
} from '@treelab/panda';

import { IToastProps } from '../modules/core/types';
import { getUploadTokenV2 } from '../modules/auth/network';

interface IPathOptions {
  workspaceId: string;
  blockId: string;
}

interface IUploadArgs {
  cellId?: string;
  files: File[];
  maxFileCount?: number;
  maxFileSize?: number;
  pathOptions: IPathOptions;
  onSuccess: IUploadEvents['onSuccess'];
  onError?: (message: string) => void;
  onWarn?: (warns: IUploadWarn[]) => void;
}

const LIMIT_MAX_FILE_COUNT = 0;
const LIMIT_MAX_FILE_SIZE = 0;
const LIMIT_SINGLE_FILE_SIZE = 50;

export { downloadFile, downloadFiles };

const pickerObject: {
  oper?: UploadOper;
  IsInitialized: boolean;
} = {
  IsInitialized: false,
};

export const initializePandaPicker = () => {
  if (pickerObject.IsInitialized) return;

  (async () => {
    let meta = await getUploadTokenV2();

    if (meta?.code !== 'SUCCESS') {
      const msg = `get token fail, meta: ${meta}`;
      console.log(msg);
      return;
    }

    pickerObject.IsInitialized = true;
    pickerObject.oper = new UploadOper({
      meta: {
        ...meta.body,
        url: process.env.REACT_APP_DOWNLOAD_ATTACHMENT_URL,
        uploadUrl: process.env.REACT_APP_UPLOAD_ATTACHMENT_URL,
      },
      maxFileCount: LIMIT_MAX_FILE_COUNT,
      maxFileSize: LIMIT_MAX_FILE_SIZE,
      singleFileSize: LIMIT_SINGLE_FILE_SIZE * 1024 * 1024,
    });
  })();
};

export const uploadFiles = ({
  files,
  maxFileCount,
  maxFileSize,
  pathOptions: { workspaceId, blockId },
  onSuccess,
  onError,
  onWarn,
}: IUploadArgs) => {
  const { oper } = pickerObject;
  if (!oper) return;

  let queueId: string = '';
  const storeTo = `${workspaceId}/${blockId}`;
  const done = (data: IAttachment[]) => {
    if (onSuccess) onSuccess(data);
  };
  const error = (msg: string) => {
    if (onError) onError(msg);
    if (queueId) oper.clear(queueId);
  };

  queueId = oper.upload({
    files,
    onWarn,
    autoOpen: true,
    removeByError: true,
    options: {
      storeTo,
      maxFileCount,
      maxFileSize,
      onSuccess: done,
      onError: error,
    },
  });
};

export const getUploadWarn = (warns: IUploadWarn[], ignoreCodes: UploadWarnCodes[] = []) => {
  return warns.find(({ warnCode }) => !ignoreCodes.includes(warnCode));
};

const getUploadWarnMessageId = ({ warnCode }: IUploadWarn) => UPLOAD_CODES[warnCode];

export const showToastByUploadWarn = (
  setToast: (params: { toast: IToastProps; persist?: boolean }) => void,
  warn?: IUploadWarn,
) => {
  if (!warn) return;

  const toast: IToastProps = {
    messageId: getUploadWarnMessageId(warn),
    isError: true,
    values: { file: warn.fileName },
  };

  switch (warn.warnCode) {
    case UploadWarnCodes.MAX_COUNT:
      toast.values!.count = LIMIT_MAX_FILE_COUNT;
      break;
    case UploadWarnCodes.MAX_FILE_SIZE:
      toast.values!.size = LIMIT_MAX_FILE_SIZE;
      break;
    case UploadWarnCodes.SINGLE_FILE_SIZE:
      toast.values!.size = LIMIT_SINGLE_FILE_SIZE;
      break;
  }

  setToast({ toast });
};

export const showUploadWarnToast =
  (
    setToast: (params: { toast: IToastProps; persist?: boolean }) => void,
    ignoreCodes: UploadWarnCodes[] = [],
  ) =>
  (warns: IUploadWarn[]) =>
    showToastByUploadWarn(setToast, getUploadWarn(warns, ignoreCodes));
